/**
 * Service for tag lists and tag recommendation/suggestion endpoints.
 *
 * Responsibilities:
 * - Fetch full tags list (v1).
 * - Fetch trending tags (v2, unwraps {tags}).
 * - Fetch recommended tags for a gif.
 * - Fetch tag suggestions with optional query/count.
 * - Fetch matched tags for a query.
 */
import type { ApiClient } from "@/common/api/http/types";
import {
  ITag,
  ITagList,
  ITagSuggestedOptions,
  ITagLandingFeed,
} from "../types";

interface IGetRecommendedParams {
  gifId: string;
  page?: number;
}

interface IGetMatchedTagsParams {
  query: string;
}

export default class TagsService {
  public constructor(private readonly client: ApiClient) {}

  async getTags(): Promise<ITagList> {
    return await this.client.get("v1/tags");
  }

  async getTrendingTags(): Promise<ITag[]> {
    const res = await this.client.get<Promise<{ tags: ITag[] }>>("v2/tags/trending");
    return res.tags;
  }

  async getRecommendedTags(params: IGetRecommendedParams) {
    return await this.client.get(`v2/recommend/tags/${params.gifId}?page=${params.page}`);
  }

  async getTagSuggested({ count, query }: ITagSuggestedOptions): Promise<string[]> {
    const params = new URLSearchParams();

    if (query) {
      params.append("query", query);
    }

    if (count) {
      params.append("count", count);
    }

    return await this.client.get(`v2/tags/suggest?${params.toString()}`);
  }

  getMatchedTags({ query }: IGetMatchedTagsParams): Promise<string[]> {
    const queryParams = new URLSearchParams();
    queryParams.append("query", query);
    return this.client.get(`v1/tags/match?${queryParams.toString()}`);
  }

  async searchSuggestions(payload: string) {
    return await this.client.get(`v1/tags/suggest?query=${payload}`);
  }

  async getTagLandingFeed(params: { tag: string }): Promise<ITagLandingFeed> {
    return await this.client.get(`/v2/tags/feed/${params.tag}`);
  }
}
